import React, { useState, useTransition } from "react";

import { Moment } from "moment-timezone";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import classNames from "classnames";

import { WithApolloV2Client } from "providers/WithApolloV2Client";
import Button from "ui/Button";
import SuspenseBoundary from "ui/SuspenseBoundary";

import LogLineContent from "./LogLineContent";
import LogLineDetails from "./LogLineDetails";
import { useLogLinesV2SuspenseQuery } from "./gql/LogLines.v2.generated";
import type { LogLineFieldsFragment } from "./gql/LogLineContent.v2.generated";

type Props = {
  serverId: string;
  databaseId?: string;
  classification?: string | null;
  queryFingerprint?: string;
  postgresRoleId?: string;
  backendPid?: number;
  /** Unbounded if omitted */
  from?: Moment;
  /** Unbounded (up to now) if omitted */
  to?: Moment;
};

function LogLines(props: Props) {
  return (
    <WithApolloV2Client>
      <SuspenseBoundary>
        <LogLineList {...props} />
      </SuspenseBoundary>
    </WithApolloV2Client>
  );
}

const PAGE_SIZE = 100;

function LogLineList({
  serverId,
  databaseId,
  classification,
  queryFingerprint,
  postgresRoleId,
  backendPid,
  from,
  to,
}: Props) {
  const [isLoadingMore, startLoadingMore] = useTransition();
  const permalinkedId = useLocation().hash.slice(1);
  const { data, fetchMore } = useLogLinesV2SuspenseQuery({
    variables: {
      serverId,
      databaseId,
      classification,
      queryFingerprint,
      postgresRoleId,
      backendPid,
      first: PAGE_SIZE,
      from: from?.toISOString(),
      to: to?.toISOString(),
    },
  });

  const connection = data.server.logLines;
  const { hasNextPage, endCursor } = connection.pageInfo;

  return (
    <div>
      <ul
        className={classNames(
          "bg-black text-white font-mono text-xs p-[10px] overflow-x-scroll m-0",
          !hasNextPage && "rounded-b-[5px]",
        )}
      >
        {connection.edges.map((edge) => (
          <LogLineRow
            key={edge.node.id}
            node={edge.node}
            defaultExpanded={edge.node.id === permalinkedId}
            serverId={serverId}
            databaseId={databaseId}
          />
        ))}
        {connection.edges.length === 0 && "No recent log entries"}
      </ul>
      {hasNextPage && (
        // startTransition keeps the current lines visible while the next page
        // loads (instead of the Suspense fallback blanking the list)
        <Button
          bare
          variant="link"
          className="ml-2 my-1"
          disabled={isLoadingMore}
          onClick={() =>
            startLoadingMore(() => {
              fetchMore({ variables: { after: endCursor } });
            })
          }
        >
          {isLoadingMore ? "Loading…" : "Load older logs"}
        </Button>
      )}
    </div>
  );
}

function LogLineRow({
  node,
  defaultExpanded,
  serverId,
  databaseId,
}: {
  node: LogLineFieldsFragment;
  defaultExpanded: boolean;
  serverId: string;
  databaseId?: string;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <li className="list-none whitespace-nowrap leading-4 max-w-[70vw] group">
      <span
        className={classNames(
          "inline-block w-[15px] -ml-[5px] opacity-80 hover:opacity-100",
          expanded ? "visible" : "invisible group-hover:visible",
        )}
      >
        <Button
          bare
          aria-label={expanded ? "Collapse log line" : "Expand log line"}
          onClick={() => setExpanded(!expanded)}
        >
          <FontAwesomeIcon icon={expanded ? faCircleXmark : faCirclePlus} />
        </Button>
      </span>
      {expanded ? (
        <SuspenseBoundary>
          <LogLineDetails
            serverId={serverId}
            logLineId={node.id}
            databaseId={databaseId}
          />
        </SuspenseBoundary>
      ) : (
        <LogLineContent
          logLine={node}
          serverId={serverId}
          databaseId={databaseId}
        />
      )}
    </li>
  );
}

export default LogLines;
